import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    ImageItem,
    LayerItem,
    SceneV2,
    TemplateDto,
    TemplateOrientation,
    TemplateSceneV2,
    TextItem,
    VideoItem,
    WebsiteItem,
    selectTemplateLayout,
} from "@exibra/shared";

/** Default upper bound for the rendered preview height (keeps vertical templates from overflowing). */
const DEFAULT_MAX_HEIGHT = 720;

interface V2ScenePreviewProps {
    /** The v2 template being previewed. */
    template: TemplateDto;
    /** The v2 scene payload (kept separate so the type is narrowed by the caller). */
    scene: TemplateSceneV2;
    /** Placeholder values supplied by the screen configuration. */
    placeholders: Record<string, string>;
    /** Device display format; selects the matching layout when the template has an alternate. */
    format?: TemplateOrientation | null;
    /** Forwarded to the outer container (max width, border, …). */
    style?: CSSProperties;
    /** Upper bound for the rendered height. */
    maxHeight?: number;
    /**
     * Renders a still of the first scene only — no slideshow advancement and no video autoplay
     * (the poster/first frame is shown instead). Used for cheap grid thumbnails.
     */
    staticPreview?: boolean;
}

/**
 * Renders a v2 template as a scaled DOM slideshow, mirroring how the presenter displays it.
 *
 * The legacy Konva preview only draws text and image layers on a fixed 16:9 stage, so v2 templates
 * (which are dimension-driven and whose scenes are usually a full-bleed video, image, or website)
 * rendered blank. This component scales each scene to the template's own dimensions and renders the
 * same layer types the presenter does, advancing through scenes the way the device would.
 */
export const V2ScenePreview = ({
    template,
    scene,
    placeholders,
    format,
    style,
    maxHeight = DEFAULT_MAX_HEIGHT,
    staticPreview = false,
}: V2ScenePreviewProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
    const [activeIndex, setActiveIndex] = useState(0);

    // Pick the layout that matches the device's orientation; falls back to the primary layout.
    const layout = useMemo(() => selectTemplateLayout(scene, format), [scene, format]);
    const { dimensions, scenes } = layout;

    // Restart the slideshow whenever the template or layout changes.
    useEffect(() => {
        setActiveIndex(0);
    }, [template.id, layout.orientation, scenes.length]);

    useEffect(() => {
        const updateSize = () => {
            if (containerRef.current) {
                setContainerSize({
                    width: containerRef.current.clientWidth,
                    height: containerRef.current.clientHeight,
                });
            }
        };

        updateSize();

        const resizeObserver = new ResizeObserver(updateSize);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }
        window.addEventListener("resize", updateSize);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener("resize", updateSize);
        };
    }, []);

    // Fit the template into the available box, preserving its aspect ratio. When the container has
    // no intrinsic height (e.g. width-driven flow layout), fall back to the maxHeight cap.
    const scale = useMemo(() => {
        if (containerSize.width <= 0 || dimensions.width <= 0 || dimensions.height <= 0) {
            return 0;
        }
        const heightBound = containerSize.height > 0 ? containerSize.height : maxHeight;
        return Math.min(containerSize.width / dimensions.width, heightBound / dimensions.height);
    }, [containerSize, dimensions.width, dimensions.height, maxHeight]);

    const advance = useCallback(() => {
        setActiveIndex((current) => (scenes.length === 0 ? 0 : (current + 1) % scenes.length));
    }, [scenes.length]);

    const activeScene = scenes[activeIndex] ?? scenes[0];

    return (
        <div
            ref={containerRef}
            className="flex items-center justify-center overflow-hidden"
            style={style}
        >
            {scale > 0 && activeScene && (
                <SceneView
                    key={activeScene.id}
                    scene={activeScene}
                    templateScene={scene}
                    dimensions={dimensions}
                    scale={scale}
                    placeholders={placeholders}
                    isOnlyScene={scenes.length <= 1}
                    staticPreview={staticPreview}
                    onAdvance={advance}
                />
            )}
        </div>
    );
};

interface SceneViewProps {
    scene: SceneV2;
    templateScene: TemplateSceneV2;
    dimensions: { width: number; height: number };
    scale: number;
    placeholders: Record<string, string>;
    isOnlyScene: boolean;
    staticPreview: boolean;
    onAdvance: () => void;
}

/**
 * Renders a single v2 scene and schedules advancement to the next scene.
 */
const SceneView = ({
    scene,
    templateScene,
    dimensions,
    scale,
    placeholders,
    isOnlyScene,
    staticPreview,
    onAdvance,
}: SceneViewProps) => {
    const videoElement = useMemo(
        () => scene.elements.find((element): element is VideoItem => element.type === "video"),
        [scene.elements],
    );
    const photoDurationMs = scene.durationMs ?? templateScene.defaultPhotoDurationMs;

    // Non-video scenes advance on a timer; video scenes advance when the clip ends (below).
    // A static preview never advances.
    useEffect(() => {
        if (videoElement || isOnlyScene || staticPreview) {
            return;
        }
        const timer = setTimeout(onAdvance, photoDurationMs);
        return () => clearTimeout(timer);
    }, [isOnlyScene, onAdvance, photoDurationMs, scene.id, staticPreview, videoElement]);

    return (
        <div
            style={{
                backgroundColor: "#000",
                height: dimensions.height * scale,
                overflow: "hidden",
                position: "relative",
                width: dimensions.width * scale,
            }}
        >
            {scene.elements.map((layer, index) => (
                <PreviewLayer
                    key={layer.id}
                    layer={layer}
                    scale={scale}
                    zIndex={scene.elements.length - index}
                    placeholders={placeholders}
                    loop={Boolean(videoElement && videoElement.id === layer.id && isOnlyScene)}
                    staticPreview={staticPreview}
                    onVideoEnded={!isOnlyScene ? onAdvance : undefined}
                />
            ))}
        </div>
    );
};

interface PreviewLayerProps {
    layer: LayerItem;
    scale: number;
    zIndex: number;
    placeholders: Record<string, string>;
    loop: boolean;
    staticPreview: boolean;
    onVideoEnded?: () => void;
}

/**
 * Renders one v2 layer as scaled, absolutely-positioned DOM.
 */
const PreviewLayer = ({
    layer,
    scale,
    zIndex,
    placeholders,
    loop,
    staticPreview,
    onVideoEnded,
}: PreviewLayerProps) => {
    const style = createLayerStyle(layer, scale, zIndex);

    if (layer.type === "image") {
        const image = layer as ImageItem;
        const src = resolveAssetUrl(image.imageUrl);
        if (!src) return null;
        return (
            <img alt="" draggable={false} src={src} style={{ ...style, objectFit: "contain" }} />
        );
    }

    if (layer.type === "video") {
        const video = layer as VideoItem;
        const src = resolveAssetUrl(video.videoUrl);
        if (!src) return null;
        // A static preview shows the poster still rather than autoplaying the clip.
        const poster = resolveAssetUrl(video.posterUrl);
        if (staticPreview) {
            if (poster) {
                return (
                    <img
                        alt=""
                        draggable={false}
                        src={poster}
                        style={{ ...style, objectFit: "cover" }}
                    />
                );
            }
            return (
                <video
                    src={src}
                    poster={poster}
                    style={{ ...style, objectFit: "cover" }}
                    muted
                    playsInline
                    controls={false}
                    preload="metadata"
                />
            );
        }
        return (
            <video
                src={src}
                poster={poster}
                style={{ ...style, objectFit: "cover" }}
                autoPlay
                muted
                loop={loop}
                playsInline
                controls={false}
                onEnded={loop ? undefined : onVideoEnded}
                // Don't let a still-processing/broken clip freeze a multi-scene slideshow.
                onError={loop ? undefined : onVideoEnded}
            />
        );
    }

    if (layer.type === "text") {
        const text = layer as TextItem;
        const displayText = text.isPlaceholder
            ? placeholders[text.id] || `[${text.text ?? text.name}]`
            : text.text;
        return (
            <div
                style={{
                    ...style,
                    color: "#000",
                    fontFamily: text.style?.font || "Arial",
                    fontSize: (text.style?.fontSize ?? 16) * scale,
                    fontStyle: text.style?.italic ? "italic" : "normal",
                    fontWeight: text.style?.bold ? 700 : 400,
                    lineHeight: 1.2,
                    opacity: text.isPlaceholder && !placeholders[text.id] ? 0.5 : 1,
                    overflow: "hidden",
                    textAlign: text.style?.align ?? "left",
                    whiteSpace: "pre-wrap",
                }}
            >
                {displayText ?? ""}
            </div>
        );
    }

    if (layer.type === "website") {
        const website = layer as WebsiteItem;
        return (
            <iframe
                src={website.websiteUrl}
                title={website.name}
                style={{ ...style, border: 0, backgroundColor: "#fff" }}
                sandbox="allow-forms allow-modals allow-popups allow-same-origin allow-scripts"
            />
        );
    }

    return null;
};

/**
 * Converts a v2 layer's design-space coordinates into an absolutely-positioned DOM style.
 */
function createLayerStyle(layer: LayerItem, scale: number, zIndex: number): CSSProperties {
    return {
        height: (layer.height ?? 0) * scale || undefined,
        left: (layer.x ?? 0) * scale,
        position: "absolute",
        top: (layer.y ?? 0) * scale,
        transform: `rotate(${layer.rotation ?? 0}deg) scale(${layer.scaleX ?? 1}, ${layer.scaleY ?? 1})`,
        transformOrigin: "top left",
        userSelect: "none",
        width: (layer.width ?? 0) * scale || undefined,
        zIndex,
    };
}

/**
 * Resolves a presenter asset URL (relative API path) into something the browser can load.
 */
function resolveAssetUrl(url: string | undefined): string {
    if (!url) return "";
    if (url.startsWith("http") || url.startsWith("blob:") || url.startsWith("data:")) {
        return url;
    }
    return `${import.meta.env.VITE_API_URL ?? ""}${url}`;
}
